import { useParams, useLocation, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageSEO from "@/components/feature/PageSEO";
import { articles } from "@/mocks/articles";
import { articleSeoMap } from "@/mocks/articleSeo";

const siteUrl = typeof import.meta !== "undefined" && import.meta.env ? (import.meta.env.VITE_SITE_URL as string) || "https://acbuyspreadsheet.casa" : "https://acbuyspreadsheet.casa";

function buildArticleJsonLd(article: typeof articles[0]) {
  const articleUrl = `${siteUrl}/${article.slug}`;
  const jsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.imageUrl,
    "datePublished": article.publishedDate,
    "author": {
      "@type": "Organization",
      "name": "ACBuy Spreadsheet",
    },
    "publisher": {
      "@type": "Organization",
      "name": "ACBuy Spreadsheet",
      "url": siteUrl,
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl,
    },
  };

  if (article.faqs.length > 0) {
    return {
      ...jsonLd,
      "hasPart": {
        "@type": "FAQPage",
        "mainEntity": article.faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer,
          },
        })),
      },
    };
  }

  return jsonLd;
}

export default function ArticlePage() {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const location = useLocation();
  const slug = paramSlug || location.pathname.replace(/^\//, "").split("/")[0];
  const article = articles.find((a) => a.slug === slug);
  const seoData = articleSeoMap[slug];

  if (!article) {
    return (
      <>
        <PageSEO
          title="Article Not Found — ACBuy Spreadsheet"
          description="The requested acbuyspreadsheet article was not found. Return to the homepage to browse all product categories and SEO resources."
          path={location.pathname}
        />
        <Navbar />
        <main className="pt-24 pb-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <Link to="/" className="text-accent-600 hover:text-accent-700 underline cursor-pointer">
              Return to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = buildArticleJsonLd(article);

  return (
    <>
      <PageSEO
        title={seoData?.metaTitle || article.title}
        description={seoData?.metaDescription || article.excerpt}
        path={`/${article.slug}`}
        ogImage={seoData?.ogImage || article.imageUrl}
        ogType="article"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        <article>
          {/* Article Header */}
          <header className="pt-24 pb-10 md:pt-32 md:pb-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <nav className="text-xs text-foreground-400 mb-4 flex items-center gap-2 flex-wrap">
                <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">
                  Home
                </Link>
                <span>/</span>
                <span className="text-foreground-600">{article.tag}</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed max-w-3xl">
                Published: {article.publishedDate} &middot; {article.readTime}
              </p>
            </div>
          </header>

          {/* Featured Image */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
            <div className="rounded-3xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Article Body */}
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Main Content */}
              <div className="w-full lg:w-[62%]">
                <div className="text-foreground-700 leading-relaxed space-y-6">
                  {article.sections.map((section, sIdx) => {
                    if (section.type === "heading") {
                      return (
                        <section key={sIdx}>
                          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                            {section.heading}
                          </h2>
                        </section>
                      );
                    }
                    if (section.type === "paragraph") {
                      return (
                        <section key={sIdx}>
                          {section.paragraphs?.map((p, pIdx) => (
                            <p
                              key={pIdx}
                              className="text-sm md:text-base"
                              {...(pIdx > 0 ? { style: { marginTop: "1rem" } } : {})}
                              dangerouslySetInnerHTML={{
                                __html: p
                                  .replace(
                                    /acbuy spreadsheet homepage/gi,
                                    '<a href="/" style="color:oklch(var(--accent-600));text-decoration:underline">acbuy spreadsheet homepage</a>'
                                  )
                                  .replace(
                                    /acbuy spreadsheet guide/gi,
                                    '<a href="/acbuy-spreadsheet-guide" style="color:oklch(var(--accent-600));text-decoration:underline">acbuy spreadsheet guide</a>'
                                  )
                                  .replace(/acbuy spreadsheet/gi, '<strong>acbuy spreadsheet</strong>')
                                  .replace(/acbuyspreadsheet/gi, '<strong>acbuyspreadsheet</strong>'),
                              }}
                            ></p>
                          ))}
                        </section>
                      );
                    }
                    if (section.type === "table") {
                      return (
                        <section key={sIdx}>
                          {section.heading && (
                            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                              {section.heading}
                            </h3>
                          )}
                          <div className="overflow-x-auto rounded-xl border border-background-200/70">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-background-100">
                                  {section.tableHeaders?.map((h, hIdx) => (
                                    <th key={hIdx} className="text-left p-3 font-semibold text-foreground-800 whitespace-nowrap">
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {section.tableRows?.map((row, rIdx) => (
                                  <tr key={rIdx} className="border-t border-background-200/60">
                                    {row.cells.map((cell, cIdx) => (
                                      <td
                                        key={cIdx}
                                        className={`p-3 whitespace-nowrap ${
                                          row.highlight ? "font-semibold text-foreground-900" : "text-foreground-600"
                                        }`}
                                      >
                                        {cIdx === 1 && typeof cell === "string" && ["Fast", "Daily", "Weekly", "High", "Very High"].some(v => cell.includes(v)) ? (
                                          <span className="bg-accent-100 text-accent-700 text-xs px-2 py-0.5 rounded-full">
                                            {cell}
                                          </span>
                                        ) : cIdx === 2 && typeof cell === "string" && cell === "High" ? (
                                          <span className="text-accent-600 font-semibold">{cell}</span>
                                        ) : (
                                          cell
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {section.tableCaption && (
                            <p className="text-xs text-foreground-400 mt-2">{section.tableCaption}</p>
                          )}
                        </section>
                      );
                    }
                    return null;
                  })}

                  {/* FAQ Section */}
                  {article.faqs.length > 0 && (
                    <section>
                      <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                        Frequently Asked Questions
                      </h2>
                      <div className="space-y-5">
                        {article.faqs.map((faq, fIdx) => (
                          <div key={fIdx}>
                            <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1">
                              {faq.question}
                            </h3>
                            <p className="text-sm text-foreground-600">{faq.answer}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Internal Link CTA */}
                  <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70 mt-8">
                    <p className="text-sm text-foreground-600">
                      <strong className="text-foreground-900">Ready to start sourcing?</strong> Return to
                      the <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium cursor-pointer">acbuy spreadsheet homepage</Link> to
                      browse all fourteen product categories, or visit the{" "}
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-accent-600 hover:text-accent-700 underline font-medium cursor-pointer"
                      >
                        main platform
                      </a>{" "}
                      for the full product sourcing experience.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-[38%]">
                <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70 sticky top-24">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                    Related Resources
                  </h3>

                  <div className="space-y-4">
                    <Link to="/" className="block group cursor-pointer">
                      <h4 className="text-sm font-medium text-foreground-800 group-hover:text-accent-600 transition-colors">
                        ACBuy Spreadsheet Homepage
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">
                        Browse all categories and start your sourcing journey.
                      </p>
                    </Link>

                    {articles
                      .filter((a) => a.slug !== article.slug)
                      .slice(0, 4)
                      .map((related) => (
                        <div key={related.slug} className="border-t border-background-200/60 pt-4">
                          <Link to={`/${related.slug}`} className="block group cursor-pointer">
                            <h4 className="text-sm font-medium text-foreground-800 group-hover:text-accent-600 transition-colors">
                              {related.title}
                            </h4>
                            <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                              {related.excerpt}
                            </p>
                          </Link>
                        </div>
                      ))}

                    <div className="border-t border-background-200/60 pt-4">
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="block w-full text-center bg-primary-500 text-background-50 text-sm font-medium py-3 rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                      >
                        Visit Main Platform
                        <i className="ri-external-link-line ml-1.5 text-xs"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 mt-16 mb-8">
            <div className="bg-background-100 rounded-3xl p-8 md:p-12 text-center border border-background-200/70">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                Ready to Source Smarter?
              </h3>
              <p className="mt-3 text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
                Visit the acbuyspreadsheet platform to access verified product listings and start
                making data-driven buying decisions today.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-foreground-900 text-background-50 text-sm font-medium px-7 py-3 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
                >
                  acbuy spreadsheet
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-foreground-900 text-background-50 text-sm font-medium px-7 py-3 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
                >
                  buy it now
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}